import { ChatCircle, CheckCircle, UserPlus } from "@phosphor-icons/react";

import { type ProjectFeedEvent } from "../../../services/askPunttProjectFeed.types";
import {
  resolveFeedApprovalHeadline,
  resolveFeedApprovalNote,
  resolveFeedApprovalReasoning,
} from "../utils/feedApprovalNote";

function iconFor(type: ProjectFeedEvent["type"]) {
  if (type === "position_recorded") return CheckCircle;
  if (type === "approval_note") return ChatCircle;
  return UserPlus;
}

function positionLabel(position: string | null): string | null {
  if (position === "approved") return "Approved";
  if (position === "changes_requested") return "Changes requested";
  return null;
}

/**
 * Timeline card for fluid participation history — who was suggested and why,
 * who weighed in, and reviewer notes. Presentational only.
 */
export function FeedApprovalCard({ event }: { event: ProjectFeedEvent }) {
  const titleId = `feed-approval-title-${event._id}`;
  const Icon = iconFor(event.type);
  const payload = event.payload ?? {};
  const reasoning = resolveFeedApprovalReasoning(
    payload,
    event.body,
    event.type,
  );
  const position =
    typeof payload.position === "string" ? payload.position : null;
  const noteText = resolveFeedApprovalNote(
    payload,
    event.body,
    position,
    event.type,
  );
  const assetLabel =
    typeof payload.assetLabel === "string" ? payload.assetLabel : null;
  const actorName = event.actor?.name?.trim() || null;
  const verdict = positionLabel(position);
  const headline = resolveFeedApprovalHeadline(
    event.type,
    event.body,
    position,
  );

  return (
    <section
      className="rounded-xl border border-puntt-neutral-gray-5 bg-base-white p-3.5"
      data-testid={`feed-approval-card-${event._id}`}
      data-event-type={event.type}
      aria-labelledby={titleId}
    >
      <div className="flex items-start gap-2.5">
        <span
          aria-hidden="true"
          className="flex size-8 shrink-0 items-center justify-center rounded-full bg-puntt-neutral-gray-2 text-puntt-neutral-gray-11"
        >
          <Icon size={16} weight="fill" />
        </span>
        <div className="min-w-0 flex-1">
          <h3
            id={titleId}
            data-testid={titleId}
            className="text-[13px] font-semibold text-puntt-neutral-gray-12"
          >
            {headline}
          </h3>
          {actorName && event.type === "position_recorded" ? (
            <p
              className="mt-0.5 text-[12px] text-puntt-neutral-gray-11"
              data-testid={`feed-approval-actor-${event._id}`}
            >
              {actorName}
              {assetLabel ? ` · ${assetLabel}` : ""}
            </p>
          ) : assetLabel ? (
            <p
              className="mt-0.5 text-[12px] text-puntt-neutral-gray-11"
              data-testid={`feed-approval-asset-${event._id}`}
            >
              {assetLabel}
            </p>
          ) : null}
          {reasoning ? (
            <p
              className="mt-1 text-[12px] leading-relaxed text-puntt-neutral-gray-11"
              data-testid={`feed-approval-reasoning-${event._id}`}
            >
              {reasoning}
            </p>
          ) : null}
          {verdict ? (
            <p
              className="mt-1 text-[11px] font-medium uppercase tracking-wide text-puntt-neutral-gray-10"
              data-testid={`feed-approval-position-${event._id}`}
            >
              {verdict}
            </p>
          ) : null}
          {noteText ? (
            <p
              className="mt-1 text-[12px] leading-relaxed text-puntt-neutral-gray-12"
              data-testid={`feed-approval-note-${event._id}`}
              aria-label="Reviewer note"
            >
              {noteText}
            </p>
          ) : null}
        </div>
      </div>
    </section>
  );
}
